import React, { useState, useEffect, useCallback } from 'react';
import { awaabService } from '../../services/api';

// F-056 Competent Person & Contractor Register (landlord portfolio-wide).
// Covers: competent-person entries (HHSRS/CSCS/damp-remediation quals), contractor
// entries (+ insurance/DBS), 30/14/7-day expiry surfacing, per-property assignment
// history and the on-demand expiry scan trigger. The hard-block itself is enforced
// server-side when an investigation/work references a register entry.

const REGISTER_TYPES = ['PERSON', 'CONTRACTOR'];
const QUALIFICATION_TYPES = ['HHSRS', 'CSCS', 'DAMP_REMEDIATION', 'INSURANCE', 'DBS'];
const EXPIRY_WARNING_DAYS = 30;

const TYPE_LABEL: Record<string, string> = { HHSRS: 'HHSRS qualification', CSCS: 'CSCS card', DAMP_REMEDIATION: 'Damp remediation', INSURANCE: 'Insurance certificate', DBS: 'DBS check' };

const fmtDate = (iso?: string) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtDateTime = (iso?: string) => iso ? new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const qualStatus = (expiryDate?: string) => {
    if (!expiryDate) return { label: 'No expiry', cls: 'bg-gray-100 text-gray-600 border-gray-200' };
    const days = Math.ceil((new Date(expiryDate).getTime() - Date.now()) / 86400000);
    if (days < 0) return { label: 'EXPIRED', cls: 'bg-rose-50 text-rose-800 border-rose-200' };
    if (days <= EXPIRY_WARNING_DAYS) return { label: `${days}d`, cls: 'bg-amber-50 text-amber-800 border-amber-200' };
    return { label: 'Valid', cls: 'bg-emerald-50 text-emerald-800 border-emerald-200' };
};

interface QualRow { type: string; name: string; ref: string; issuedAt: string; expiryDate: string; }
const emptyQual = (type = 'HHSRS'): QualRow => ({ type, name: '', ref: '', issuedAt: '', expiryDate: '' });

export default function AwaabRegisterPanel() {
    const [entries, setEntries] = useState<any[]>([]);
    const [filter, setFilter] = useState<'ALL' | 'PERSON' | 'CONTRACTOR'>('ALL');
    const [loading, setLoading] = useState(false);
    const [scanning, setScanning] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saved, setSaved] = useState<string | null>(null);

    // Add / edit form
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editingType, setEditingType] = useState('PERSON');
    const [formOpen, setFormOpen] = useState(false);
    const [name, setName] = useState('');
    const [org, setOrg] = useState('');
    const [contactEmail, setContactEmail] = useState('');
    const [contactPhone, setContactPhone] = useState('');
    const [active, setActive] = useState(true);
    const [qualRows, setQualRows] = useState<QualRow[]>([emptyQual()]);

    // Assignment history
    const [expanded, setExpanded] = useState<string | null>(null);
    const [assignments, setAssignments] = useState<any[]>([]);
    const [propertyFilter, setPropertyFilter] = useState('');

    const flash = (msg: string) => { setSaved(msg); setTimeout(() => setSaved(null), 4000); };

    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const res = await awaabService.listRegister();
            setEntries(res.data?.data || []);
        } catch (e: any) {
            setError(e?.response?.data?.message || e?.message || 'Failed to load register');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const visible = entries.filter((e) => filter === 'ALL' || e.registerType === filter);

    const resetForm = () => {
        setEditingId(null); setEditingType('PERSON');
        setName(''); setOrg(''); setContactEmail(''); setContactPhone(''); setActive(true);
        setQualRows([emptyQual()]); setFormOpen(false);
    };

    const openEdit = (e: any) => {
        setEditingId(e.id); setEditingType(e.registerType);
        setName(e.name || ''); setOrg(e.org || '');
        setContactEmail(e.contact?.email || ''); setContactPhone(e.contact?.phone || '');
        setActive(e.active !== false);
        const quals: QualRow[] = (e.qualifications || []).map((q: any) => ({
            type: q.type, name: q.name || '', ref: q.ref || '',
            issuedAt: (q.issuedAt || '').slice(0, 10), expiryDate: (q.expiryDate || '').slice(0, 10),
        }));
        setQualRows(quals.length ? quals : [emptyQual()]);
        setFormOpen(true);
    };

    const setQual = (idx: number, field: keyof QualRow, value: string) => {
        setQualRows((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
    };

    const save = async () => {
        setLoading(true); setError(null);
        try {
            const payload = {
                registerType: editingType,
                name,
                org,
                contact: { email: contactEmail || null, phone: contactPhone || null },
                active,
                qualifications: qualRows
                    .filter((q) => q.type)
                    .map((q) => ({ type: q.type, name: q.name || undefined, ref: q.ref || undefined, issuedAt: q.issuedAt ? new Date(q.issuedAt).toISOString() : undefined, expiryDate: q.expiryDate ? new Date(q.expiryDate).toISOString() : undefined })),
            };
            if (editingId) {
                await awaabService.updateRegisterEntry(editingType, editingId, payload);
                flash('Register entry updated and re-sealed');
            } else {
                await awaabService.createRegisterEntry(payload);
                flash('Register entry created');
            }
            resetForm();
            await load();
        } catch (e: any) {
            setError(e?.response?.data?.message || e?.message || 'Failed to save register entry');
        } finally { setLoading(false); }
    };

    const runScan = async () => {
        setScanning(true); setError(null);
        try {
            const res = await awaabService.scanRegister();
            const s = res.data?.data || {};
            flash(`Expiry scan complete: ${s.scanned ?? 0} scanned, ${s.alerted ?? 0} alerted`);
        } catch (e: any) {
            setError(e?.response?.data?.message || e?.message || 'Expiry scan failed');
        } finally { setScanning(false); }
    };

    const toggleAssignments = async (e: any) => {
        if (expanded === e.id) { setExpanded(null); setAssignments([]); return; }
        setExpanded(e.id); setPropertyFilter('');
        try {
            const res = await awaabService.getRegisterAssignments(e.registerType, e.id);
            setAssignments(res.data?.data?.assignments || []);
        } catch (err: any) {
            setError(err?.response?.data?.message || 'Failed to load assignment history');
        }
    };

    const filteredAssignments = propertyFilter ? assignments.filter((a) => a.propertyId === propertyFilter) : assignments;

    return (
        <div className="mt-4 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h4 className="text-sm font-semibold text-gray-900"><i className="fas fa-id-card text-orange-500 mr-2"></i>Competent Person & Contractor Register</h4>
                    <p className="text-xs text-gray-500 mt-0.5">
                        HHSRS-qualified surveyors and competent contractors. Expired qualifications block new assignments and trigger 30/14/7-day alerts.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={runScan} disabled={scanning}
                        className="px-3 py-1.5 rounded-md text-xs font-medium bg-teal-50 text-teal-700 border border-teal-200 hover:bg-teal-100 disabled:opacity-50">
                        <i className="fas fa-bell mr-1.5"></i>{scanning ? 'Scanning…' : 'Run expiry scan'}
                    </button>
                    <button onClick={() => { resetForm(); setFormOpen(true); }}
                        className="px-3 py-1.5 rounded-md text-xs font-medium bg-orange-500 text-white hover:bg-orange-600">
                        <i className="fas fa-plus mr-1.5"></i>Add entry
                    </button>
                </div>
            </div>

            {error && <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700"><i className="fas fa-exclamation-circle mr-2"></i>{error}</div>}
            {saved && <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm text-emerald-700"><i className="fas fa-check-circle mr-2"></i>{saved}</div>}

            {/* Add / edit form */}
            {formOpen && (
                <div className="rounded-lg border border-orange-200 bg-orange-50/30 p-4">
                    <h5 className="text-sm font-semibold text-gray-900 mb-3"><i className="fas fa-pen-to-square text-orange-500 mr-2"></i>{editingId ? 'Edit register entry' : 'Add register entry'}</h5>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
                        <div>
                            <label className="block text-[11px] font-medium text-gray-600 mb-1">Type</label>
                            <select value={editingType} onChange={e => setEditingType(e.target.value)} disabled={!!editingId}
                                className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm disabled:bg-gray-100">
                                {REGISTER_TYPES.map(t => <option key={t} value={t}>{t === 'PERSON' ? 'Competent person' : 'Contractor'}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-[11px] font-medium text-gray-600 mb-1">Name</label>
                            <input value={name} onChange={e => setName(e.target.value)} className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm" placeholder="e.g. Ada Surveyor / Bob Builds Ltd" />
                        </div>
                        <div>
                            <label className="block text-[11px] font-medium text-gray-600 mb-1">Organisation</label>
                            <input value={org} onChange={e => setOrg(e.target.value)} className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm" placeholder="e.g. SafeSurvey Ltd" />
                        </div>
                        <div>
                            <label className="block text-[11px] font-medium text-gray-600 mb-1">Active</label>
                            <div className="flex items-center h-[34px]">
                                <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} className="h-4 w-4" />
                                <span className="ml-2 text-xs text-gray-500">{active ? 'Assignable' : 'Suspended'}</span>
                            </div>
                        </div>
                        <div>
                            <label className="block text-[11px] font-medium text-gray-600 mb-1">Contact email</label>
                            <input value={contactEmail} onChange={e => setContactEmail(e.target.value)} className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm" />
                        </div>
                        <div>
                            <label className="block text-[11px] font-medium text-gray-600 mb-1">Contact phone</label>
                            <input value={contactPhone} onChange={e => setContactPhone(e.target.value)} className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm" />
                        </div>
                    </div>

                    <p className="text-[11px] font-semibold text-gray-600 uppercase mb-1">Qualifications / certificates</p>
                    <div className="space-y-2 mb-3">
                        {qualRows.map((q, idx) => (
                            <div key={idx} className="grid grid-cols-2 md:grid-cols-6 gap-2">
                                <select value={q.type} onChange={e => setQual(idx, 'type', e.target.value)}
                                    className="md:col-span-1 border border-gray-300 rounded-md px-2 py-1.5 text-sm">
                                    {QUALIFICATION_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
                                </select>
                                <input value={q.name} onChange={e => setQual(idx, 'name', e.target.value)} className="md:col-span-2 border border-gray-300 rounded-md px-2 py-1.5 text-sm" placeholder="Name / cert title" />
                                <input value={q.ref} onChange={e => setQual(idx, 'ref', e.target.value)} className="md:col-span-1 border border-gray-300 rounded-md px-2 py-1.5 text-sm" placeholder="Ref no." />
                                <input type="date" value={q.issuedAt} onChange={e => setQual(idx, 'issuedAt', e.target.value)} className="border border-gray-300 rounded-md px-2 py-1.5 text-sm" />
                                <div className="flex items-center gap-1">
                                    <input type="date" value={q.expiryDate} onChange={e => setQual(idx, 'expiryDate', e.target.value)} className="flex-1 border border-gray-300 rounded-md px-2 py-1.5 text-sm" />
                                    {qualRows.length > 1 && (
                                        <button onClick={() => setQualRows(rows => rows.filter((_, i) => i !== idx))} className="text-rose-500 hover:text-rose-700 px-1" title="Remove row">
                                            <i className="fas fa-trash-can"></i>
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="flex items-center gap-2">
                        <button onClick={() => setQualRows(rows => [...rows, emptyQual(qualRows[0]?.type || 'HHSRS')])}
                            className="px-2 py-1 rounded-md text-xs text-orange-700 border border-orange-200 hover:bg-orange-100">
                            <i className="fas fa-plus mr-1"></i>Add qualification
                        </button>
                        <div className="flex-1"></div>
                        <button onClick={resetForm} className="px-3 py-1.5 rounded-md text-xs font-medium border border-gray-300 text-gray-600 hover:bg-gray-50">Cancel</button>
                        <button onClick={save} disabled={loading || !name}
                            className="px-4 py-1.5 rounded-md text-xs font-medium bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50">
                            {loading ? 'Saving…' : editingId ? 'Save changes' : 'Create entry'}
                        </button>
                    </div>
                </div>
            )}

            {/* Filter */}
            <div className="flex items-center gap-2 text-xs">
                {(['ALL', 'PERSON', 'CONTRACTOR'] as const).map((f) => (
                    <button key={f} onClick={() => setFilter(f)}
                        className={`px-3 py-1 rounded-full border font-medium ${filter === f ? 'bg-orange-500 text-white border-orange-500' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                        {f === 'ALL' ? 'All' : f === 'PERSON' ? 'Persons' : 'Contractors'}
                    </button>
                ))}
                <span className="ml-auto text-gray-400">{visible.length} entr{visible.length === 1 ? 'y' : 'ies'}</span>
            </div>

            {/* Entries */}
            {visible.length === 0 ? (
                <p className="text-sm text-gray-500 italic py-6 text-center">No register entries yet. Add a competent person or contractor to start building your evidence of a qualified team.</p>
            ) : (
                <div className="space-y-2">
                    {visible.map((e) => (
                        <div key={e.id} className="rounded-lg border border-gray-200 p-3">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ring-1 ${e.registerType === 'PERSON' ? 'bg-blue-50 text-blue-800 border-blue-200' : 'bg-indigo-50 text-indigo-800 border-indigo-200'}`}>
                                        {e.registerType === 'PERSON' ? 'Competent person' : 'Contractor'}
                                    </span>
                                    <span className="text-sm font-medium text-gray-900">{e.name}</span>
                                    {e.org && <span className="text-xs text-gray-500">{e.org}</span>}
                                    <span className={`text-[10px] font-semibold ${e.active === false ? 'text-rose-600' : 'text-emerald-600'}`}>{e.active === false ? 'SUSPENDED' : 'ACTIVE'}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button onClick={() => toggleAssignments(e)} className="text-xs text-gray-500 hover:text-gray-700">
                                        <i className="fas fa-history mr-1"></i>{(e.assignments || []).length} assignments
                                    </button>
                                    <button onClick={() => openEdit(e)} className="px-2 py-1 rounded-md text-xs border border-gray-300 text-gray-600 hover:bg-gray-50">
                                        <i className="fas fa-pen mr-1"></i>Edit
                                    </button>
                                </div>
                            </div>

                            <div className="mt-2 flex flex-wrap gap-1.5">
                                {(e.qualifications || []).length === 0 && <span className="text-xs text-gray-400 italic">No qualifications recorded</span>}
                                {(e.qualifications || []).map((q: any, i: number) => {
                                    const st = qualStatus(q.expiryDate);
                                    return (
                                        <span key={i} title={TYPE_LABEL[q.type] || q.type} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ring-1 ${st.cls}`}>
                                            <span>{q.name || TYPE_LABEL[q.type] || q.type}</span>
                                            {q.expiryDate && <span className="opacity-70">· {fmtDate(q.expiryDate)}</span>}
                                            <span className="opacity-90">· {st.label}</span>
                                        </span>
                                    );
                                })}
                            </div>

                            {expanded === e.id && (
                                <div className="mt-3 rounded-md border border-gray-100 bg-gray-50 p-3">
                                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                        <p className="text-xs font-semibold text-gray-700">Assignment history</p>
                                        <input value={propertyFilter} onChange={e => setPropertyFilter(e.target.value)}
                                            className="border border-gray-300 rounded-md px-2 py-1 text-xs w-56" placeholder="Filter by propertyId (repeat visits)" />
                                    </div>
                                    {filteredAssignments.length === 0 ? (
                                        <p className="text-xs text-gray-500 italic">No assignments recorded.</p>
                                    ) : (
                                        <div className="max-h-48 overflow-y-auto rounded-md border border-gray-100 bg-white">
                                            <table className="min-w-full divide-y divide-gray-100 text-xs">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-2 py-1.5 text-left font-medium text-gray-500">Role</th>
                                                        <th className="px-2 py-1.5 text-left font-medium text-gray-500">Case</th>
                                                        <th className="px-2 py-1.5 text-left font-medium text-gray-500">Property</th>
                                                        <th className="px-2 py-1.5 text-left font-medium text-gray-500">Assigned</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100">
                                                    {filteredAssignments.map((a: any, i: number) => (
                                                        <tr key={i}>
                                                            <td className="px-2 py-1.5 text-gray-700">{a.role.replace(/_/g, ' ')}</td>
                                                            <td className="px-2 py-1.5 font-mono text-gray-600">{(a.caseId || '').slice(0, 12)}…</td>
                                                            <td className="px-2 py-1.5 text-gray-600">{a.propertyAddress || a.propertyId || '—'}</td>
                                                            <td className="px-2 py-1.5 text-gray-500">{fmtDateTime(a.assignedAt)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
